/**
 * useTranscribeCapture — voice capture via browser-direct AWS Transcribe.
 *
 * getUserMedia → AudioWorklet (48k→16k PCM) → TranscribeStreamingSession, with
 * final transcript segments delivered to onTranscript. Exposes the same shape
 * as useDictation ({isListening, isSupported, toggle, stop}) so the capture
 * wizard can pick between engines. Only enabled when the Identity Pool is
 * configured AND the browser supports AudioWorklet; otherwise isSupported is
 * false and the caller falls back to Web Speech dictation.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { TranscribeStreamingSession } from './transcribeStreamingService';
import { isTranscribeConfigured } from './awsClients';

const WORKLET_URL = '/audio-resampler-worklet.js';
const PROCESSOR_NAME = 'audio-resampler-processor';

/** Whether the browser has the Web Audio + AudioWorklet APIs we need (pure). */
export function audioWorkletSupported(win: typeof globalThis | undefined = typeof window === 'undefined' ? undefined : window): boolean {
  if (!win) return false;
  const w = win as unknown as {
    AudioContext?: unknown;
    AudioWorkletNode?: unknown;
    navigator?: { mediaDevices?: { getUserMedia?: unknown } };
  };
  return Boolean(w.AudioContext && w.AudioWorkletNode && w.navigator?.mediaDevices?.getUserMedia);
}

interface UseTranscribeCaptureOptions {
  onTranscript: (transcript: string) => void;
  lang?: string;
  onError?: (error: Error) => void;
}

interface UseTranscribeCaptureReturn {
  isListening: boolean;
  isSupported: boolean;
  toggle: () => void;
  stop: () => void;
}

export function useTranscribeCapture({ onTranscript, lang, onError }: UseTranscribeCaptureOptions): UseTranscribeCaptureReturn {
  const [isListening, setIsListening] = useState(false);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const sessionRef = useRef<TranscribeStreamingSession | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const nodeRef = useRef<AudioWorkletNode | null>(null);
  // Generation token: bumped by stop() so an in-flight start() that resolves
  // after a stop/unmount aborts and releases whatever it acquired, rather than
  // leaving the mic live and streaming to AWS.
  const genRef = useRef(0);

  const isSupported = isTranscribeConfigured() && audioWorkletSupported();

  const stop = useCallback(() => {
    genRef.current += 1; // cancel any in-flight start()
    nodeRef.current?.port.postMessage('stop');
    nodeRef.current?.disconnect();
    nodeRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    ctxRef.current?.close().catch(() => { /* AudioContext already closing; nothing to recover */ });
    ctxRef.current = null;
    sessionRef.current?.stop();
    sessionRef.current = null;
    setIsListening(false);
  }, []);

  const start = useCallback(async () => {
    const gen = ++genRef.current; // claim this run; supersedes any prior in-flight start
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: { ideal: 48000 } },
      });
      if (gen !== genRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      ctx = new AudioContext();
      await ctx.audioWorklet.addModule(WORKLET_URL);
      if (gen !== genRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        await ctx.close().catch(() => { /* AudioContext already closing; nothing to recover */ });
        return;
      }

      streamRef.current = stream;
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, PROCESSOR_NAME);
      nodeRef.current = node;

      const session = new TranscribeStreamingSession(lang || navigator.language || 'en-GB', {
        onTranscript: (fragment) => {
          if (fragment.isFinal) onTranscriptRef.current(fragment.text);
        },
        onError: (err) => {
          onErrorRef.current?.(err);
          stop();
        },
        onClose: () => {},
      });
      sessionRef.current = session;

      node.port.onmessage = (event: MessageEvent) => {
        if (event.data instanceof Uint8Array) session.pushAudio(event.data);
      };

      // source → worklet → destination keeps the graph pulling; the worklet
      // writes no output, so nothing is played back (no echo).
      source.connect(node);
      node.connect(ctx.destination);

      // Not awaited: start() runs the transcribe loop until stop(). The .catch
      // is a backstop for a synchronous throw before the session's try/catch.
      void session.start().catch((err) => {
        onErrorRef.current?.(err instanceof Error ? err : new Error('Transcription failed'));
        stop();
      });
      setIsListening(true);
    } catch (err) {
      stream?.getTracks().forEach((t) => t.stop());
      if (ctx) await ctx.close().catch(() => { /* AudioContext already closing; nothing to recover */ });
      throw err;
    }
  }, [lang, stop]);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
      return;
    }
    start().catch((err) => {
      onErrorRef.current?.(err instanceof Error ? err : new Error('Microphone unavailable'));
      stop();
    });
  }, [isListening, start, stop]);

  useEffect(() => () => stop(), [stop]);

  return { isListening, isSupported, toggle, stop };
}
